import { DownloadIcon } from '@components/Icons/Download';
import { LoadingActionButton } from '@components/LoadingActionButton/LoadingActionButton';
import {
  ActionDrawerItem,
  MobileActionDrawer,
} from '@components/MobileActionDrawer/MobileActionDrawer';
import { PriceDisplay } from '@components/PriceDisplay/PriceDisplay';
import { handlePortalClick } from '@lib/purchase';
import { describeSubscriptionStatus, planAccessFromBilling } from '@lib/utils/planAccess';
import { formatExpiresIn } from '@lib/utils/relativeTime';
import { formatCreditsWithUnit, formatDate } from '@lib/utils/units';
import {
  ArrowUpward,
  BlockRounded,
  ErrorRounded,
  MoreHorizRounded,
  ReceiptLongRounded,
} from '@mui/icons-material';
import { billingStore } from '@stores/Billing';
import { trackingStore } from '@stores/Tracking';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import css from './Billing.module.scss';
import { CreditsUsage } from './CreditsUsage';
import { InvoiceItem } from './InvoiceItemRow';
import { InvoiceList } from './InvoiceList';
import { StorageUsage } from './StorageUsage';

// ── Billing Actions (shared between desktop buttons and mobile drawer) ──

type BillingAction = 'download-all' | 'cancel-subscription' | 'manage-invoices';

const BILLING_ACTIONS: (ActionDrawerItem & { id: BillingAction })[] = [
  { id: 'download-all', label: 'Download all', icon: <DownloadIcon /> },
  {
    id: 'cancel-subscription',
    label: 'Cancel your subscription',
    icon: <BlockRounded />,
    danger: true,
  },
  { id: 'manage-invoices', label: 'Manage your invoices', icon: <ReceiptLongRounded /> },
];

export const Billing = observer(({ mobile }: { mobile?: boolean }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigateTo = useNavigate();

  const handleBillingAction = (action: BillingAction) => {
    setDrawerOpen(false);
    switch (action) {
      case 'download-all':
        handlePortalClick();
        break;
      case 'cancel-subscription':
        trackingStore.cancelSubscriptionClicked();
        handlePortalClick();
        break;
      case 'manage-invoices':
        trackingStore.manageInvoicesClicked();
        handlePortalClick();
        break;
    }
  };

  if (billingStore.error)
    return <span>Error loading billing data, {billingStore.error?.message}...</span>;
  if (!billingStore.data) return <span>Loading, please wait...</span>;

  // Live, not the paywall's latched verdict: this card states what the account is now.
  const planAccess = planAccessFromBilling(billingStore.data);
  const subscription = billingStore.data.subscription;
  const product = subscription?.product ?? null;

  // Convert creditNotes to InvoiceItem format
  const invoices: InvoiceItem[] = (billingStore.data?.creditNotes || []).map((creditNote) => {
    const createdAt = new Date(creditNote.createdAt);
    const formattedDate = formatDate(creditNote.createdAt);
    const timestamp = createdAt.toISOString().replace(/[-:T]/g, '').split('.')[0];
    const invoiceName = `Invoice ${timestamp} - ${formattedDate}`;
    const invoiceNumber = creditNote.invoiceId;

    return {
      id: creditNote._id,
      invoiceName,
      invoiceNumber: invoiceNumber,
      creditsObtained: creditNote.credits.originalAmount,
      creditsUsed: creditNote.credits.originalAmount - creditNote.credits.currentAmount,
      transactionDate: creditNote.createdAt,
      expiryDate: creditNote.expiresAt,
      type: creditNote.origin,
    };
  });

  // The next note that will expire with credits still on it. A past or spent note has nothing
  // left to lose, and used to read "0 credits will expire within the day".
  const soonestExpiringCreditNote =
    billingStore.creditNotesSortedByExpiryDate.find(
      (creditNote) => creditNote.expiresAt > Date.now() && creditNote.credits.currentAmount > 0,
    ) ?? null;

  return (
    <div className={css.billingContainer}>
      <div className={css.billingSummary}>
        <div className={classNames(css.billingCard, css.subscriptionInfo)}>
          {subscription ? (
            <>
              <div className={css.subscriptionInfoTop}>
                <div className={css.subscriptionInfoLeft}>
                  <div className={css.header}>
                    {product?.name ?? 'Subscription'}{' '}
                    {product?.price.interval === 'month' ? <span>Billed monthly</span> : null}
                  </div>
                  <div className={css.subheader}>
                    {product?.description || 'Your current subscription.'}
                  </div>
                </div>
                {product && <PriceDisplay price={product.price} />}
              </div>
              <div className={classNames(css.subscriptionInfo, css.subscriptionInfoCreditsReset)}>
                {subscription.termEnd ? (
                  <span>
                    The current billing period ends on {formatDate(subscription.termEnd)}.
                  </span>
                ) : null}
                {subscription.status === 'active' ? (
                  <span>Your subscription will renew on the next billing cycle.</span>
                ) : null}
                {planAccess === 'past-due' ? (
                  <span>
                    Your last payment didn&apos;t go through. Update your card in the billing portal
                    to keep your plan.
                  </span>
                ) : null}
                {planAccess === 'lapsed' ? (
                  <span>
                    This subscription is {describeSubscriptionStatus(subscription.status)}. Fix it
                    in the billing portal to keep using StarZero.
                  </span>
                ) : null}
                {planAccess === 'past-due' || planAccess === 'lapsed' ? (
                  <LoadingActionButton
                    variant="primary"
                    onClick={() => handlePortalClick('billing-settings')}
                    loadingText="Opening the billing portal..."
                  >
                    Open billing portal
                  </LoadingActionButton>
                ) : null}
              </div>
              <div
                className={classNames(css.subscriptionInfo, css.subscriptionInfoUpgradeReminder)}
              >
                <div className={css.subscriptionInfoUpgradeReminderLeft}>
                  {billingStore.creditNotesSortedByExpiryDate.length > 0 && (
                    <>
                      {soonestExpiringCreditNote && (
                        <>
                          <span className={css.subscriptionInfoUpgradeReminderIcon}>
                            <ErrorRounded />
                          </span>{' '}
                          <span className={css.subscriptionInfoUpgradeReminderText}>
                            <span>
                              {formatCreditsWithUnit(
                                soonestExpiringCreditNote.credits.currentAmount,
                              )}{' '}
                              will expire {formatExpiresIn(soonestExpiringCreditNote.expiresAt)}.
                            </span>
                            <span className={css.usageSubheader}>
                              Credits that expire sooner are consumed first.
                            </span>
                          </span>
                        </>
                      )}
                      {/* <LoadingActionButton
                        variant="secondary"
                        color="secondary"
                        className={css.subscriptionInfoUpgradeReminderManageSubscription}
                        onClick={handleCreditsPurchase}
                        loadingText="Loading billing portal..."
                      >
                        {`Add ${formatCreditsWithUnit(300000)}`}
                      </LoadingActionButton> */}
                      <a
                        href="/pricing"
                        className={css.pricingLink}
                        onClick={(e) => {
                          e.preventDefault();
                          trackingStore.upgradeClicked({ source: 'billing-settings' });
                          navigateTo('/pricing');
                        }}
                      >
                        Upgrade plan
                        <ArrowUpward className={css.arrowIcon} />
                      </a>
                    </>
                  )}
                </div>
                <span>&nbsp;</span>
              </div>
            </>
          ) : (
            <div className={css.subscriptionInfoTop}>
              <div className={css.subscriptionInfoLeft}>
                <div className={css.header}>No plan</div>
                <div className={css.subheader}>StarZero needs a plan on your account.</div>
              </div>
              <LoadingActionButton
                variant="primary"
                onClick={() => navigateTo('/subscribe')}
                loadingText="Loading plans..."
              >
                Choose a plan
              </LoadingActionButton>
            </div>
          )}
        </div>
        <div className={classNames(css.billingCard, css.creditsLeftCard)}>
          <div className={css.creditsLeftTop}>
            <div className={css.creditsLeftTopLeft}>
              <div className={css.header}>Usage</div>
              <div className={css.subheader}>Track your usage to avoid surprises.</div>
            </div>
          </div>
          <div className={css.creditsLeftBottom}>
            <CreditsUsage />
            <StorageUsage />
          </div>
        </div>
      </div>
      <div className={css.billingAndInvoicing}>
        <div className={css.billingAndInvoicingHeader}>
          <div>
            <div className={css.billingAndInvoicingTitleRow}>
              <h3>Billing and invoices</h3>
              {mobile && (
                <button className={css.kebabButton} onClick={() => setDrawerOpen(true)}>
                  <MoreHorizRounded />
                </button>
              )}
            </div>
            {mobile && (
              <p className={css.billingAndInvoicingSubheader}>
                Pick an account plan that fits your workflow.
              </p>
            )}
          </div>
          {!mobile && (
            <div className={css.billingAndInvoicingHeaderRight}>
              <LoadingActionButton
                variant="secondary"
                onClick={() => {
                  trackingStore.cancelSubscriptionClicked();
                  return handlePortalClick();
                }}
                loadingText="Loading your subscription page..."
              >
                Cancel your subscription
              </LoadingActionButton>
              <LoadingActionButton
                variant="primary"
                onClick={() => {
                  trackingStore.manageInvoicesClicked();
                  return handlePortalClick();
                }}
                loadingText="Loading your invoice page..."
              >
                Manage your invoices
              </LoadingActionButton>
            </div>
          )}
        </div>
        {mobile && (
          <MobileActionDrawer
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            onOpen={() => setDrawerOpen(true)}
            title="Billing and invoices"
            actions={BILLING_ACTIONS}
            onAction={handleBillingAction}
          />
        )}
        <div className={css.tableScrollContainer}>
          <InvoiceList invoices={invoices} />
        </div>
      </div>
    </div>
  );
});
